import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildJournalPrint } from "@/modules/docs/print/doc-print-math";
import { useOrgLetterhead } from "@/modules/docs/print/use-org-letterhead";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { DataTable } from "@/modules/shell/data-table";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { DeskDocTabs } from "@/modules/shell/DeskDocTabs";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { RemindMeButton } from "@/modules/shell/RemindMeButton";
import { useDeskDocTab } from "@/modules/shell/use-desk-doc-tab";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { fetchJournal } from "@/shared/api/accounting";
import { docLink } from "@/shared/doc-link";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { journalLineColumns } from "./journal-detail-columns";

const JV_DOC_TABS = ["journal", "lines"] as const;

export function JournalEntryDetailPage() {
  const { companySlug, journalId } = useParams();
  const { letterhead } = useOrgLetterhead(companySlug);
  const [docTab, setDocTab] = useDeskDocTab("journal", JV_DOC_TABS);

  const journalQuery = useQuery({
    queryKey: queryKeys.journals.detail(companySlug ?? "", journalId ?? ""),
    enabled: Boolean(companySlug && journalId),
    queryFn: () => fetchJournal(companySlug!, journalId!),
  });
  const errorMsg = queryErrorMessage(journalQuery.error, journalQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const data = journalQuery.data;
  const je = data?.journal;

  useRecordRecentDoc({
    companySlug,
    kind: "journalEntry",
    id: je?.id,
    label: je?.number,
    path:
      companySlug && je?.id
        ? `/c/${companySlug}/gl/journals/${je.id}`
        : undefined,
    hint: je?.memo ?? je?.kind,
  });

  if (!companySlug || !journalId) return null;

  const displayError =
    errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  if (journalQuery.isPending || !data || !je) {
    return (
      <PageFrame width="narrow" className="py-4">
        {displayError ? (
          <Alert variant="destructive">{displayError}</Alert>
        ) : (
          <Alert variant="muted">Loading journal…</Alert>
        )}
      </PageFrame>
    );
  }

  const { lines } = data;
  const sourceHref = docLink(
    companySlug,
    je.sourceDocType,
    je.sourceDocId,
  );
  const printData = buildJournalPrint({
    number: je.number,
    lifecycle: je.lifecycle,
    kind: je.kind,
    memo: je.memo,
    sourceDocType: je.sourceDocType,
    totalDebit: data.totalDebit,
    totalCredit: data.totalCredit,
    issuedAt: je.entryDate,
    lines: lines.map((l) => ({
      accountCode: l.accountCode,
      accountName: l.accountName,
      debit: l.debit,
      credit: l.credit,
      memo: l.memo,
    })),
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} letterhead={letterhead} />
      </div>
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/gl/trial-balance`}
            listLabel="Trial balance"
          />
        </div>
        <PageHeader
          eyebrow="Journal"
          title={je.number}
          description={[je.kind, je.memo].filter(Boolean).join(" · ")}
          actions={
            <div className="flex flex-wrap gap-2">
              <RemindMeButton
                companySlug={companySlug}
                kind="journal_entry"
                docId={journalId}
                docNumber={je.number}
                partyName={je.memo}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
            </div>
          }
        />
        <div className="mt-2">
          <LifecycleStatus value={je.lifecycle} showLabel />
        </div>
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="journal_entry"
          docId={journalId}
          linkPath={`/c/${companySlug}/gl/journals/${journalId}`}
          docLabel={[je.number, je.memo].filter(Boolean).join(" · ")}
        />
        <DeskDocTabs
          aria-label="Journal views"
          value={docTab}
          onChange={setDocTab}
          items={[
            { id: "journal", label: "Journal" },
            { id: "lines", label: "Lines", count: lines.length },
          ]}
        />
      </div>

      {docTab === "journal" && (
        <>
          <FieldGroup title="Money" className="mt-6" level="sub">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-[var(--color-muted)]">Debit</dt>
              <dd className="m-0 font-mono tabular-nums">
                {moneyDisplay(data.totalDebit)}
              </dd>
              <dt className="text-[var(--color-muted)]">Credit</dt>
              <dd className="m-0 font-mono tabular-nums">
                {moneyDisplay(data.totalCredit)}
              </dd>
              <dt className="text-[var(--color-muted)]">Currency</dt>
              <dd className="m-0">{data.currencyCode}</dd>
            </dl>
          </FieldGroup>
          <FieldGroup title="Summary" className="mt-6" level="sub">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-[var(--color-muted)]">Date</dt>
              <dd className="m-0">{je.entryDate}</dd>
              {je.postedAt ? (
                <>
                  <dt className="text-[var(--color-muted)]">Posted</dt>
                  <dd className="m-0 text-xs text-[var(--color-muted)]">
                    {new Date(je.postedAt).toLocaleString()}
                  </dd>
                </>
              ) : null}
              {je.sourceDocType ? (
                <>
                  <dt className="text-[var(--color-muted)]">Source</dt>
                  <dd className="m-0">
                    {sourceHref ? (
                      <Link to={sourceHref} className="no-underline">
                        {je.sourceDocType}
                      </Link>
                    ) : (
                      je.sourceDocType
                    )}
                  </dd>
                </>
              ) : null}
            </dl>
          </FieldGroup>
          {je.notes ? (
            <AdvancedSection className="no-print mt-4" subtitle="notes">
              <FieldGroup title="Notes" level="sub">
                <p className="m-0 text-sm">{je.notes}</p>
              </FieldGroup>
            </AdvancedSection>
          ) : null}
        </>
      )}

      {docTab === "lines" && (
        <>
          <DataTable
            listId="journal-detail-lines"
            companySlug={companySlug}
            className="mt-4"
            columns={journalLineColumns(companySlug)}
            data={lines}
            getRowId={(r) => r.id}
            manualSorting={false}
            empty={
              <EmptyState
                title="No lines"
                description="This journal has no account lines."
              />
            }
          />
          <p className="mt-3 m-0 flex flex-wrap justify-between gap-3 text-sm font-semibold">
            <span>Total · {data.currencyCode}</span>
            <span className="font-mono tabular-nums">
              Dr {moneyDisplay(data.totalDebit)} · Cr{" "}
              {moneyDisplay(data.totalCredit)}
            </span>
          </p>
        </>
      )}

      {displayError ? (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      ) : null}

      <DocActivity
        companySlug={companySlug}
        docType="journal_entry"
        docId={journalId}
        linkPath={`/c/${companySlug}/gl/journals/${journalId}`}
      />
    </PageFrame>
  );
}
